<template>
  <m-dropdown
    :value="open"
    :disabled="readOnly"
    :title="$t('listFilter.title')"
    placement="bottomLeft"
    @update:value="emit('update:open', $event)"
  >
    <slot name="trigger" />
    <template #overlay>
      <m-card
        class="quick-date-scope"
        no-padding
      >
        <m-content :padding-y="4">
          <m-content :padding-x="5">
            <quick-filter-header
              :label="scopeItem.text"
              @delete="emit('delete')"
              @add-to-advanced="emit('addToAdvanced')"
            >
              <template #op>
                <m-select
                  :value="selectedType"
                  :items="intervalTypes"
                  :read-only="readOnly"
                  xs
                  hide-border
                  light
                  :trigger-style="{ textTransform: 'lowercase' }"
                  @input="updateSelectedType"
                />
              </template>
            </quick-filter-header>
            <div
              v-if="!isEmpty"
              :class="['_input', showNumberField ? '-number' : '', showDateField ? '-date' : '']"
            >
              <m-select
                v-if="hasDynamicValues"
                class="_op"
                :value="selectedDynamicType"
                :items="dynamicTypes"
                :read-only="readOnly"
                full-width
                @input="updateSelectedDynamicType"
              />
              <m-input-number
                v-if="showNumberField"
                class="_number"
                :value="amount"
                :read-only="readOnly"
                nullable
                :min="0"
                full-width
                @input="updateAmount"
              />
              <template v-if="showDateField">
                <m-date-picker
                  v-if="showDateSingleField"
                  :value="dateSingle"
                  :date-time="dateTimeConfig"
                  :read-only="readOnly"
                  :placeholder="$t('general.empty')"
                  clearable
                  full-width
                  @input="updateDateRange"
                />
                <m-date-picker
                  v-if="showDateRangeField"
                  :value="dateRange"
                  :date-time="dateTimeConfig"
                  :read-only="readOnly"
                  :placeholder="$t('general.empty')"
                  range
                  clearable
                  full-width
                  @input="updateDateRange"
                />
              </template>
            </div>
          </m-content>
        </m-content>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script setup>
import QuickFilterHeader from '@/components/filter/quick/QuickFilterHeader.vue';
import useScopeItem from '@/composables/filter/scope-item';
import { BETWEEN, EXACT_DATE, GT, LAST_X_DAYS, LT, NEXT_X_DAYS } from '@/lib/constants';
import { DateTime } from 'luxon';
import { computed } from 'vue';
import { fromISO } from 'shared/lib/time';
import { getType, isEmptyType, isNotEmptyType } from '@/lib/filter/dynamic-date';
import { propertyType } from 'shared/constants.json';

const dateTimeConfig = DateTime;

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  scopeItem: {
    type: Object,
    required: true,
  },
  node: {
    type: Object,
    required: true,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  hasDynamicValues: {
    type: Boolean,
    default: false,
  },
  hideIsEmpty: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['update:open', 'update:scope', 'delete', 'addToAdvanced']);

const { operatorTypes: getOperatorTypes } = useScopeItem();
const intervalTypes = computed(() => getOperatorTypes(propertyType.date, { hideEmpty: props.hideIsEmpty }));

const scope = computed(() => props.node.children[0].scope);
const timeRange = computed(() => scope.value.timeRange);
const isEmpty = computed(() => isEmptyType(timeRange.value) || isNotEmptyType(timeRange.value));
const selectedType = computed(() => getType(timeRange.value));
const updateSelectedType = (value) => {
  emit('update:scope', { ...scope.value, timeRange: { ...timeRange.value, type: value } });
};

const selectedDynamicType = computed(() => timeRange.value.dynamicType);
const dynamicTypes = computed(() => intervalTypes.value.find((t) => t.value === selectedType.value)?.dynamicTypes ?? []);
const updateSelectedDynamicType = (value) => {
  emit('update:scope', { ...scope.value, timeRange: { ...timeRange.value, dynamicType: value } });
};

const showNumberField = computed(() => [LAST_X_DAYS, NEXT_X_DAYS].includes(selectedDynamicType.value));
const amount = computed(() => timeRange.value.amount);
const updateAmount = (value) => {
  emit('update:scope', { ...scope.value, timeRange: { ...timeRange.value, amount: value } });
};

const showDateField = computed(() => !props.hasDynamicValues || [EXACT_DATE].includes(selectedDynamicType.value));
const showDateSingleField = computed(() => [GT, LT].includes(selectedType.value));
const dateSingle = computed(() => {
  if (selectedDynamicType.value !== EXACT_DATE) {
    return null;
  }
  switch (selectedType.value) {
    case LT:
      return fromISO(timeRange.value.end);
    case GT:
      return fromISO(timeRange.value.start);
    default:
      return null;
  }
});

const showDateRangeField = computed(() => [BETWEEN].includes(selectedType.value));
const dateRange = computed(() => ({ startDate: timeRange.value.start, endDate: timeRange.value.end }));
const updateDateRange = (value) => {
  emit('update:scope', { ...scope.value, timeRange: { ...timeRange.value, start: value.startDate, end: value.endDate } });
};

</script>

<style scoped lang="scss">
.quick-date-scope {
  ._input {
    margin: 0 .4rem .4rem .4rem;
    display: flex;
    gap: .6rem;

    &.-number {
      flex-direction: row;
    }

    &.-date {
      flex-direction: column;
    }
  }
}
</style>
